import { logger } from "../../debug";
import { transformEvent, transformProducts } from "./transformation";
import {
  PageEvent,
  ProductAddedRemovedEvent,
  PurchaseEvent,
  EventHandlerMap,
  IdentifyEvent,
  ProductViewedEvent,
} from "./types/hightouch";
import {
  AddToCart,
  LAM2,
  ProductViewed,
  Purchase,
  RemoveFromCart,
} from "./types/lam";

function pageView(data: LAM2, anonymousId: string): PageEvent {
  const transformed = transformEvent(data, anonymousId);

  return {
    ...transformed,
    category: "",
    name: "",
    properties: { title: data.context.document.title },
  };
}

function addToCart(
  data: AddToCart,
  anonymousId: string
): ProductAddedRemovedEvent {
  if (data.payload.collections?.products === undefined) {
    logger.error("[Hightouch plugin]: LAM add to cart is missing products");
    return;
  }

  const transformed = transformEvent(data, anonymousId);
  const products = transformProducts(data.payload.collections.products, true);

  return {
    ...transformed,
    event: "product_added",
    properties: {
      ...products[0],
      currency: data.context.app.currency,
    },
  };
}

function removeFromCart(
  data: RemoveFromCart,
  anonymousId: string
): ProductAddedRemovedEvent {
  if (data.payload.collections?.products === undefined) {
    logger.error(
      "[Hightouch plugin]: LAM remove from cart is missing products"
    );
    return;
  }

  const transformed = transformEvent(data, anonymousId);
  const products = transformProducts(data.payload.collections.products, true);

  return {
    ...transformed,
    event: "product_removed",
    properties: {
      ...products[0],
      currency: data.context.app.currency,
    },
  };
}

function purchase(data: Purchase, anonymousId: string): PurchaseEvent {
  if (data.payload.collections?.transaction === undefined) {
    logger.error("[Hightouch Plugin] LAM purchase is missing transaction");
    return;
  }

  const transaction = data.payload.collections.transaction;

  const transformed = transformEvent(data, anonymousId);
  const products = transformProducts(transaction.products, true);

  return {
    ...transformed,
    event: "order_completed",
    properties: {
      order_id: transaction.id,
      total: Number(transaction.total.sum),
      shipping: Number(transaction.total.shipping),
      tax: Number(transaction.total.tax),
      revenue: Number(transaction.total.subTotal),
      discount: Number(transaction.discount),
      coupon: transaction.promoCode,
      products,
      currency: data.context.app.currency,
    },
  };
}

function loginSuccess(data: LAM2, anonymousId: string): IdentifyEvent {
  const transformed: IdentifyEvent = transformEvent(data, anonymousId);

  if (data.payload.user.profile.oktaId) {
    transformed.traits = {
      oktaId: data.payload.user.profile.oktaId,
    };
  }

  return transformed;
}

function productView(
  data: ProductViewed,
  anonymousId: string
): ProductViewedEvent {
  if (data.payload.collections?.products === undefined) {
    logger.error("[Hightouch plugin]: LAM product view is missing products");
    return;
  }

  const transformed = transformEvent(data, anonymousId);
  const products = transformProducts(data.payload.collections.products, false);

  return {
    ...transformed,
    event: "product_viewed",
    properties: {
      ...products[0],
      currency: data.context.app.currency,
    },
  };
}

const eventHandlers: EventHandlerMap = {
  "page-view": {
    type: "page",
    handler: pageView,
  },
  "add-to-cart": {
    type: "track",
    handler: addToCart,
  },
  "remove-from-cart": {
    type: "track",
    handler: removeFromCart,
  },
  purchase: {
    type: "track",
    handler: purchase,
  },
  "login-success": {
    type: "identify",
    handler: loginSuccess,
  },
  "product-view": {
    type: "track",
    handler: productView,
  },
};

export default eventHandlers;
